import React from 'react';
import { File } from '@phosphor-icons/react';
import { AnyWindowEntity, WindowEntity, WindowLayout } from 'types/windows';
import { WindowWithApplicationState } from '../../../types/appState';

import { FileSkeleton, type Note } from '../../../types';
import type { BaseEntityType } from '../../../types/entities';

import { iconService } from '../../../services/iconService';
import { useSpaceStore } from '../../../stores/spaceStore';
import { useSpaceCameraStore } from '../../../stores/spaceCameraStore';
import { entityRegistry } from '../../../stores/entityRegistry';
import { useAppRegistryStore } from '../../../stores/appRegistryStore';
import { useEntityById } from '../../../stores/entityRouter';
import useUserStore from '../../../stores/userStore';
import { readDockPreferencesFromProfile } from '../../../constants/dock';
import { getSystemBarInset } from '../../../utils/systemBarInset';
import { useMemo } from 'react';
import { Icon } from '../../Icon/Icon';
import { AppSquircle } from '../../AppSquircle';
import type { WindowSizeSpec } from '../../../types/apps';

/**
 * Centralized helper to extract entityId from a window.
 * Priority 1: Root-level entityId — the canonical reference (always use this).
 * Priority 2: Legacy applicationState per-app fields — fallback for apps that have not yet
 *   been migrated to set window.entityId directly.
 *
 * Migration status:
 *   - DocsApp: migrated (sets entityId on open/close)
 *   - NotesMarkdownApp: pending — still uses applicationState.notes
 *   - PDFiumApp: pending — may use applicationState.pdfium.entityId
 */
export function getWindowEntityId(window: WindowEntity): string | undefined {
  if (window.entityId) return window.entityId;

  const legacyId =
    window.applicationState?.docs?.activeDocId ||
    window.applicationState?.pdfium?.entityId ||
    window.applicationState?.notes?.activeNoteId ||
    window.applicationState?.notes?.selectedNoteId ||
    window.applicationState?.entityView?.entityId;

  if (legacyId && process.env.NODE_ENV === 'development') {
    // This log helps identify apps that still need to be migrated to set window.entityId
    console.debug('[getWindowEntityId] Using legacy applicationState fallback — migrate app to set window.entityId directly', {
      windowId: (window as any).id,
      appType: window.appType,
      legacyId,
    });
  }

  return legacyId;
}

/**
 * Centralized helper to extract entityLlmId from a window
 */
export function getWindowEntityLlmId(window: WindowEntity): string | undefined {
  return window.entityLlmId;
}

/**
 * Resolve the entityType of the entity backing a window pane, when it's bound to one.
 * Unlike entityId/entityLlmId, entityType isn't stored directly on the window — it must
 * be looked up via the entity registry through spaceStore's getWindowEntity.
 */
export function getWindowEntityType(window: WindowEntity): string | undefined {
  return useSpaceStore.getState().getWindowEntity((window as any).id)?.entityType;
}


export const generateWindowId = () => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).slice(2, 8);
  return `window-${timestamp}-${random}`;
};

export const getAppTitle = (appTypeOrWindow: string | WindowWithApplicationState): string => {
  // If an object is passed, it might be a window with application state
  if (typeof appTypeOrWindow === 'object') {
    const window = appTypeOrWindow;
    
    // Check for docs application state
    if (window.applicationState?.docs) {
      const docsState = window.applicationState.docs;
      
      // If we're editing a document, prefer entity/recentDocs name over transient CRDT name to avoid duplication
      if (docsState.currentState === 'editing') {
        if (docsState.recentDocs && docsState.activeDocId) {
          const activeDoc = docsState.recentDocs.find((doc) => doc.docId === docsState.activeDocId);
          if (activeDoc?.name) return activeDoc.name;
        }
        if (docsState.currentDocName) return docsState.currentDocName;
      }
      
      // Default docs title
      return 'Documents';
    }
    
    // For other app types, get the app type from the window
    const appType = window.appType || '';
    // Use the appType string to lookup a title (avoid recursion)
    return getAppTitleByType(appType);
  }
  
  // String-based app type lookup
  return getAppTitleByType(appTypeOrWindow);
};

// Helper function to get app title by type string
export const getAppTitleByType = (appType: string): string => {
  // The app registry is the source of truth for installed app display names.
  // Consult it first so dynamically-registered apps not enumerated in the switch
  // below resolve to their real name instead of falling through to "Window".
  const registryName = useAppRegistryStore.getState().apps[appType]?.skeleton?.name;
  if (registryName) return registryName;

  switch (appType) {
    case 'chat':
      return 'Chat';
    case 'system':
    case 'entityBrowser':
      return 'Entity Browser';
    case 'entityBrowser2':
      return 'Drive';
    case 'console':
      return 'Console';
    case 'pdf':
      return 'PDF Viewer';
    case 'pdfium':
      return 'PDFium Viewer';
    case 'notes':
      return 'Notes';
    case 'docs':
      return 'Documents';
    case 'browser':
      return 'Browser';
    case 'webdoc':
      return 'Web Document';
    case 'settings':
      return 'Settings';
    case 'imagestudio':
    case 'mediaviewer':
      return 'Image Studio';
    case 'socialstudio':
      return 'Social Studio';
    case 'bank':
      return 'Bank';
    case 'bookkeeper':
      return 'Bookkeeper';
    case 'disco':
      return 'Disco - Data Analysis';
    case 'prompt':
      return 'Prompt';
    case 'google-drive-file':
      return 'Google Drive File';
    case 'mail':
      return 'Mail';
    case 'contacts':
      return 'Contacts';
    case 'automations':
      return 'Automations';
    case 'notifications':
      return 'Notifications';
    case 'tasks':
      return 'Tasks';
    case 'goals':
      return 'Goals';
    case 'app-builder':
      return 'App Builder';
    case 'admin-dashboard':
      return 'Admin';
    case 'prospect':
      return 'Prospecting';
    case 'sales-integrations':
      return 'Sales Integrations';
    case 'pong':
      return 'Pong';
    case 'rapids':
      return 'Rapids';
    default:
      return 'Window';
  }
};

export const getAppTypeByExt = (ext: string): string | null => {
  switch (ext.toLowerCase()) {
    case '.pdf':
      return 'pdfium';
    case '.txt':
    case '.md':
    case '.mmd':
    case '.log': // Common text file
      return 'file-viewer'; // ✅ Text files in readonly viewer
    case '.jpg':
    case '.jpeg':
    case '.png':
    case '.gif':
    case '.webp':
    case '.bmp':
      return 'imagestudio';
    case '.mp4':
    case '.webm':
    case '.mov':
    case '.avi':
    case '.mkv':
      return 'imagestudio'; // Core media app for video
    // ✅ Code files in readonly viewer
    case '.py':
    case '.js':
    case '.ts':
    case '.tsx':
    case '.jsx':
    case '.java':
    case '.cpp':
    case '.c':
    case '.h':
    case '.cs':
    case '.go':
    case '.rs':
    case '.php':
    case '.rb':
    case '.swift':
    case '.html':
    case '.css':
    case '.scss':
    case '.sass':
    case '.less':
    case '.xml':
    case '.yaml':
    case '.yml':
    case '.toml':
    case '.ini':
    case '.cfg':
    case '.conf':
    case '.sh':
    case '.bash':
    case '.zsh':
    case '.fish':
    case '.sql':
    case '.r':
    case '.lua':
    case '.perl':
    case '.dockerfile':
    case '.makefile':
    case '.cmake':
    case '.vim':
      return 'file-viewer';
    // ✅ Data files in readonly viewer
    case '.json':
    case '.csv':
      return 'file-viewer';
    // ✅ Audio files in readonly viewer
    case '.mp3':
    case '.wav':
    case '.ogg':
    case '.flac':
    case '.aac':
    case '.m4a':
      return 'file-viewer'; // Show metadata/unsupported message
    // ✅ Archive files in readonly viewer
    case '.zip':
    case '.tar':
    case '.gz':
    case '.rar':
    case '.7z':
      return 'file-viewer'; // Show metadata/unsupported message
    // ✅ Spreadsheet files to core app
    case '.xlsx':
    case '.xls':
    case '.ods':
      return 'sheets-v2';
    // ✅ Presentation files to core app
    case '.pptx':
    case '.ppt':
    case '.odp':
      return 'presentations-v2';
    default:
      return null;
  }
};

export const getAppTypeByEntity = (entity: BaseEntityType): string | null => {
  console.log('[WINDOW_UTILS] getAppTypeByEntity called:', { entityType: entity.entityType, name: entity.name });

  // VideoStudioApp — VideoProject is the routable timeline container
  if (entity.entityType === 'VideoProject') {
    console.log('[WINDOW_UTILS] Matched VideoProject, returning videostudio');
    return 'videostudio';
  }

  // DiagramApp — Diagram opens in DiagramApp window
  if (entity.entityType === 'Diagram') {
    console.log('[WINDOW_UTILS] Matched Diagram, returning diagram');
    return 'diagram';
  }

  // ChartApp — Chart opens in ChartApp window
  if (entity.entityType === 'Chart') {
    console.log('[WINDOW_UTILS] Matched Chart, returning chart');
    return 'chart';
  }

  // Rule entities open in RulesApp
  if (entity.entityType === 'Rule') {
    console.log('[WINDOW_UTILS] Matched Rule entity type, returning rules');
    return 'rules';
  }

  // Handle Mail entities - these should open in EntityViewApp for entity-specific views
  if (entity.entityType === 'Mail.Draft' || 
      entity.entityType === 'Mail.Thread' || 
      entity.entityType === 'Mail.Message' ||
      entity.entityType === 'Mail.Contact' ||
      entity.entityType === 'Mail.Audience' ||
      entity.entityType === 'Mail.Broadcast') {
    console.log('[WINDOW_UTILS] Matched Mail entity type, returning entity-view', { entityType: entity.entityType });
    return 'entity-view';
  }
  
  // Handle hierarchical PDF types directly
  if (entity.entityType === 'PDF' || 
      entity.entityType === 'File.Document.PDF' || 
      entity.entityType?.startsWith('File.Document.PDF')) {
    console.log('[WINDOW_UTILS] Matched PDF type, returning pdfium');
    return 'pdfium';
  }

  // Handle hierarchical image types directly  
  if (entity.entityType?.startsWith('File.Image')) {
    console.log('[WINDOW_UTILS] Matched image type, returning imagestudio');
    return 'imagestudio';
  }

  // Handle hierarchical video types directly  
  if (entity.entityType?.startsWith('File.Video')) {
    console.log('[WINDOW_UTILS] Matched video type, returning imagestudio');
    return 'imagestudio';
  }

  // Handle data files (CSV, JSON, XML, etc.)
  if (entity.entityType?.startsWith('File.Data')) {
    console.log('[WINDOW_UTILS] Matched data type, returning file-viewer');
    return 'file-viewer'; // ✅ NEW: Readonly file viewer with table/JSON display
  }

  // Handle code files
  if (entity.entityType?.startsWith('File.Code')) {
    console.log('[WINDOW_UTILS] Matched code type, returning file-viewer');
    return 'file-viewer'; // ✅ NEW: Readonly file viewer with syntax highlighting
  }

  // Handle text/markdown documents
  if (entity.entityType?.startsWith('File.Document.Text') || 
      entity.entityType?.startsWith('File.Document.Markdown')) {
    console.log('[WINDOW_UTILS] Matched text document type, returning file-viewer');
    return 'file-viewer'; // ✅ NEW: Readonly file viewer
  }

  // Handle spreadsheet files
  if (entity.entityType?.startsWith('File.Spreadsheet')) {
    console.log('[WINDOW_UTILS] Matched spreadsheet type, returning sheets-v2');
    return 'sheets-v2'; // Core spreadsheet app
  }

  // Handle audio/video files
  if (entity.entityType?.startsWith('File.Audio')) {
    console.log('[WINDOW_UTILS] Matched audio type, returning file-viewer');
    return 'file-viewer'; // Show in readonly viewer
  }

  if (entity.entityType?.startsWith('File.Video')) {
    console.log('[WINDOW_UTILS] Matched video type, returning imagestudio');
    return 'imagestudio'; // Core media app
  }

  // Handle archive files
  if (entity.entityType?.startsWith('File.Archive')) {
    console.log('[WINDOW_UTILS] Matched archive type, returning file-viewer');
    return 'file-viewer'; // Show in readonly viewer
  }

  // Legacy PDF detection by MIME type
  if ((entity?.skeleton as FileSkeleton)?.['@type'] === 'File' && 
      (entity?.skeleton as FileSkeleton)?.mimeType === 'application/pdf') {
    console.log('[WINDOW_UTILS] Matched legacy PDF by MIME type, returning pdfium');
    return 'pdfium';
  }

  // Handle any file type (legacy "File" or hierarchical "File.*")
  if (entity.entityType === 'File' || entity.entityType?.startsWith('File.')) {
    // Use skeleton.fileName first, fallback to entity.name
    const fileName = ((entity.skeleton as any)?.fileName || entity.name || '').toLowerCase();
    const ext = fileName.substring(fileName.lastIndexOf('.'));
    const appType = getAppTypeByExt(ext);
    console.log('[WINDOW_UTILS] File type detected:', { fileName, ext, appType });
    return appType;
  }

  console.log('[WINDOW_UTILS] No app type matched, returning null');
  return null;
};

export const stripHtml = (html: string) => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

export const getPreviewText = (note: Note) => {
  try {
    const content = note.skeleton?.content;
    if (!content) {
      return 'Empty Note';
    }

    if (typeof content === 'string') {
      return stripHtml(content).substring(0, 50) || 'Empty Note';
    }

    return 'Empty Note';
  } catch (e) {
    return 'Error loading note';
  }
};

const normalizeDisplayName = (name?: string) => {
  if (!name) return '';
  const trimmed = name.trim();
  // Only collapse duplicated file extensions (e.g., "file.pdffile.pdf"), not arbitrary text
  const len = trimmed.length;
  if (len > 1 && len % 2 === 0) {
    const half = len / 2;
    const first = trimmed.slice(0, half);
    const second = trimmed.slice(half);
    // Only collapse if it looks like a file extension duplication (contains a dot)
    if (first === second && first.includes('.')) return first;
  }
  return trimmed;
};

export const getEntityDisplayName = (entity: BaseEntityType | null | undefined) => {
  if (!entity) return 'Untitled';
  // ✅ FIX: Notes have entity.name just like all other entities - no special handling needed!
  if (entity.entityType === 'WebDocument') {
    return normalizeDisplayName((entity as any).skeleton.url);
  } else if (entity.entityType === 'Space') {
    return normalizeDisplayName(entity.name || `space_${entity._id}`);
  } else if (entity.entityType === 'File' || entity.entityType?.startsWith('File.')) {
    // entity.name is the canonical display name — updated by every rename operation.
    // skeleton.fileName is the original upload filename and is NOT updated on rename.
    // We must prefer entity.name so renames reflect immediately.
    // No normalizeDisplayName() here — filenames must preserve exact case and extension.
    return entity.name || (entity.skeleton as any)?.fileName || 'Untitled';
  } else {
    // Default: use entity.name for all entity types including Note, Doc, Group, etc.
    return normalizeDisplayName(entity.name);
  }
};

export const getEntityDisplayIcon = (entity: BaseEntityType) => {
  // Try to get icon from iconService first
  const globalIcon = iconService.getGlobalEntityIcon(entity.entityType);
  if (globalIcon && globalIcon.emoji) {
    return globalIcon.emoji;
  }
  
  // Fallback to hardcoded icons for backward compatibility
  const iconMap = {
    Space: '🃏',  // Cards emoji as fallback
    Group: '📁',
    Note: '📝',
    File: '📄',
    WebDocument: '🌐',
    Doc: '📄',
    Bank: '🏦',
  };
  
  return iconMap[entity.entityType as keyof typeof iconMap] || '⬜️'; // Default to file icon
};

export interface DockInsets {
  bottom: number;
  left: number;
  right: number;
}

const ZERO_DOCK_INSETS: DockInsets = { bottom: 0, left: 0, right: 0 };

/**
 * Space permanently reserved by the desktop dock along whichever screen edge
 * it's docked to. Maximize / edge-extend / snap all subtract this so windows
 * never sit underneath the dock.
 *
 * Mirrors native macOS: an "Always Show" dock permanently reserves space, while
 * a "Show on Hover" dock is a transient overlay that floats ABOVE window
 * content when revealed and never reserves layout space — so this
 * deliberately ignores the dock's momentary hover-shown/hidden DOM state and
 * decides purely from the persisted visibility preference. (A CSS `transform`
 * on the inner dock element for the hide animation doesn't affect the outer
 * holder's layout box anyway, so measuring the DOM mid-transition would be
 * unreliable regardless.)
 *
 * Which SIDE to reserve is derived from the dock's own measured rect rather
 * than the persisted `edge` preference — the two can disagree. Desktop's dock
 * wrapper (`DesktopShell.module.scss` `.dockRow`) is hard-coded to always
 * render at the bottom regardless of the edge preference (see the comment in
 * `DesktopShell.tsx`'s dock-visibility section), so trusting the preference
 * there would reserve the wrong side of the screen while leaving the real
 * (bottom) dock unprotected. Measuring the rect directly is correct on every
 * platform/presentation without needing to know which one is active.
 *
 * Returns all-zero when there is no persistent dock in this layout (focused
 * mode) or the dock hasn't mounted yet.
 */
export const getDockInsets = (): DockInsets => {
  const dock = document.querySelector('[data-minimize-target="dock"]') as HTMLElement | null;
  if (!dock) return ZERO_DOCK_INSETS;

  const { visibility } = readDockPreferencesFromProfile(useUserStore.getState().profile);
  if (visibility === 'hover') return ZERO_DOCK_INSETS;

  const rect = dock.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) return ZERO_DOCK_INSETS;

  // Tolerance for "touching" an edge — the dock sits a few px in from the
  // true screen edge (e.g. `.dockRow`'s `bottom: 4px`), so an exact ===0/
  // ===innerWidth check would never match.
  const EDGE_TOLERANCE = 12;
  const touchesLeft = rect.left <= EDGE_TOLERANCE;
  const touchesRight = window.innerWidth - rect.right <= EDGE_TOLERANCE;
  const touchesBottom = window.innerHeight - rect.bottom <= EDGE_TOLERANCE;

  if (touchesLeft && !touchesBottom) return { ...ZERO_DOCK_INSETS, left: Math.max(0, rect.right) };
  if (touchesRight && !touchesBottom) return { ...ZERO_DOCK_INSETS, right: Math.max(0, window.innerWidth - rect.left) };
  return { ...ZERO_DOCK_INSETS, bottom: Math.max(0, window.innerHeight - rect.top) };
};

export const getRandomWindowPositionShift = () => {
  const numbers = [-30, -20, -10, 10, 20, 30];
  return numbers[Math.floor(Math.random() * numbers.length)];
};

// Re-exported so existing imports of `getSystemBarInset` from here keep working
// unchanged; the canonical, dependency-free implementation lives in
// `utils/systemBarInset.ts`. Shared by `getUsableScreenBounds()` and the
// layout-mode cascade grid (`computeCascadeLayout`) so both agree on the same
// usable area.
export { getSystemBarInset };

/**
 * The screen-space rectangle new windows may be freely placed/sized within —
 * the full viewport minus the system bar (top) and any persistent dock
 * (left/right/bottom, via `getDockInsets`).
 *
 * IMPORTANT — this is in physical SCREEN pixels (top: the system-bar inset),
 * which is what `spatialCanvas`'s pan/zoom camera math needs (see
 * `computeSmartWindowPosition`/`getNewWindowSpawnPosition`'s spatialCanvas
 * branches: `world = (screen - pan) / zoom`). The `spatial` family, however,
 * stores `window.position` relative to `.surface`, which is itself already
 * inset below the system bar (`DesktopShell.module.scss`: `inset: 28px 0 0
 * 0;`) — so callers placing a plain floating window must convert this to
 * surface-relative space via `toSurfaceRelativeBounds` below before using it
 * as a position bound, or they'll silently allow a window to sit one
 * system-bar-height too far down (i.e. under the dock).
 */
export const getUsableScreenBounds = (): { left: number; top: number; right: number; bottom: number } => {
  const dockInsets = getDockInsets();
  return {
    left: dockInsets.left,
    top: getSystemBarInset(),
    right: window.innerWidth - dockInsets.right,
    bottom: window.innerHeight - dockInsets.bottom,
  };
};

/**
 * Convert screen-space bounds (as returned by `getUsableScreenBounds`) into
 * the surface-relative space `window.position` is stored in for the
 * `spatial` family — see the IMPORTANT note on `getUsableScreenBounds`.
 * `left`/`right` are untouched since `.surface` has no horizontal inset;
 * only `top`/`bottom` shift up by the system-bar inset.
 */
const toSurfaceRelativeBounds = (
  screenBounds: { left: number; top: number; right: number; bottom: number },
): { left: number; top: number; right: number; bottom: number } => {
  const topInset = getSystemBarInset();
  return {
    left: screenBounds.left,
    top: Math.max(0, screenBounds.top - topInset),
    right: screenBounds.right,
    bottom: Math.max(0, screenBounds.bottom - topInset),
  };
};

// Hard floor for any computed window size — matches the interactive resize
// minimum enforced by react-rnd in Window.tsx, so a freshly-created window is
// never smaller than a user could ever resize it down to.
const MIN_WINDOW_WIDTH = 200;
const MIN_WINDOW_HEIGHT = 150;

/**
 * Clamp a proposed size so it can never exceed the usable screen — the
 * viewport-percentage helpers below already scale down on small screens, but
 * this is the hard backstop that guarantees a window never gets created
 * larger than the device can actually display (e.g. a small 1366×768 laptop
 * after dock/system-bar chrome is subtracted).
 */
const clampToUsableViewport = (size: { width: number; height: number }): { width: number; height: number } => {
  const bounds = getUsableScreenBounds();
  const usableWidth = Math.max(MIN_WINDOW_WIDTH, bounds.right - bounds.left);
  const usableHeight = Math.max(MIN_WINDOW_HEIGHT, bounds.bottom - bounds.top);
  const maxWidth = Math.max(MIN_WINDOW_WIDTH, Math.round(usableWidth * 0.92));
  const maxHeight = Math.max(MIN_WINDOW_HEIGHT, Math.round(usableHeight * 0.92));
  return {
    width: Math.min(size.width, maxWidth),
    height: Math.min(size.height, maxHeight),
  };
};

/**
 * Convert a screen-pixel size into the coordinate space `window.size` must be
 * stored in for the currently-active layout family.
 *
 * `spatial` window sizes are plain screen pixels — returned unchanged.
 * `spatialCanvas` window sizes are WORLD units under a pan/zoom camera (React
 * Rnd is told the camera zoom via its `scale` prop and renders `size` at
 * `size * zoom` physical pixels — see Window.tsx), so a size computed as a
 * screen-pixel percentage must be divided by zoom or the window would render
 * larger/smaller on screen than intended as soon as the user zooms the canvas.
 */
const toActiveFamilySize = (screenSize: { width: number; height: number }): { width: number; height: number } => {
  try {
    const store = useSpaceStore.getState();
    const viewMode = store.activeSpace?.settings?.viewMode;
    if (layoutFamilyOf(viewMode) !== 'spatialCanvas') return screenSize;

    const spaceId = store.activeSpace?.id;
    if (!spaceId) return screenSize;

    const zoom = useSpaceCameraStore.getState().getCamera(spaceId).zoom || 1;
    return {
      width: Math.round(screenSize.width / zoom),
      height: Math.round(screenSize.height / zoom),
    };
  } catch {
    return screenSize;
  }
};

/**
 * Return a screen-proportional default size for new windows.
 * Fills ~58% of viewport width and ~68% of viewport height, clamped to
 * reasonable bounds so windows are comfortably sized on any screen.
 *
 * Bounds:
 *   width  — 58 % of viewport, clamped to [760, 1280]
 *   height — 68 % of viewport, clamped to [580, 960]
 *
 * The result is additionally clamped to the usable screen (see
 * `clampToUsableViewport`) so it never exceeds the space actually available
 * once the system bar / dock are accounted for.
 */
export const getDefaultWindowSize = (): { width: number; height: number } => {
  const w = Math.round(Math.min(Math.max(window.innerWidth  * 0.58, 760), 1280));
  const h = Math.round(Math.min(Math.max(window.innerHeight * 0.68, 580), 960));
  return toActiveFamilySize(clampToUsableViewport({ width: w, height: h }));
};

/**
 * Preferred default sizes keyed by entity type. Takes precedence over
 * AppDefinition.defaultWindowSize and the generic landscape default.
 * Add an entry here when a specific entity should open in a non-default shape
 * (e.g. email drafts — compact and taller-than-wide).
 */
export const ENTITY_TYPE_WINDOW_SIZES: Record<string, WindowSizeSpec> = {
  // Compact portrait composer / read view — smaller than docs, taller than wide.
  'Mail.Thread': {
    widthRatio: 0.30,
    heightRatio: 0.78,
    minWidth: 400,
    maxWidth: 520,
    minHeight: 620,
    maxHeight: 800,
    preferPortrait: true,
    portraitExtra: 100,
  },
  'Mail.Draft': {
    widthRatio: 0.30,
    heightRatio: 0.78,
    minWidth: 400,
    maxWidth: 520,
    minHeight: 620,
    maxHeight: 800,
    preferPortrait: true,
    portraitExtra: 100,
  },
};

function materializeWindowSizeSpec(spec: WindowSizeSpec): { width: number; height: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let width =
    spec.width !== undefined
      ? spec.width
      : Math.round(vw * (spec.widthRatio ?? 0.58));
  let height =
    spec.height !== undefined
      ? spec.height
      : Math.round(vh * (spec.heightRatio ?? 0.68));

  if (spec.minWidth !== undefined) width = Math.max(width, spec.minWidth);
  if (spec.maxWidth !== undefined) width = Math.min(width, spec.maxWidth);
  if (spec.minHeight !== undefined) height = Math.max(height, spec.minHeight);
  if (spec.maxHeight !== undefined) height = Math.min(height, spec.maxHeight);

  if (spec.preferPortrait) {
    height = Math.max(height, width + (spec.portraitExtra ?? 80));
  }

  return toActiveFamilySize(clampToUsableViewport({ width, height }));
}

/**
 * Resolve the preferred default window size for an app/entity open.
 *
 * Precedence:
 *   1. ENTITY_TYPE_WINDOW_SIZES[entityType] (hardcoded entity overrides)
 *   2. AppDefinition.defaultWindowSize for appType (declared on the app)
 *   3. Generic landscape default (`getDefaultWindowSize`)
 */
export function resolveDefaultWindowSize(opts?: {
  appType?: string;
  entityType?: string;
}): { width: number; height: number } {
  const entityType = opts?.entityType;
  if (entityType && ENTITY_TYPE_WINDOW_SIZES[entityType]) {
    return materializeWindowSizeSpec(ENTITY_TYPE_WINDOW_SIZES[entityType]);
  }

  const appType = opts?.appType;
  if (appType) {
    try {
      // Lazy require avoids a circular import with appDefinitions.index → apps → windowUtils.
      const { APP_DEFINITIONS_BY_ID } = require('../../../apps/appDefinitions.index') as typeof import('../../../apps/appDefinitions.index');
      const def = APP_DEFINITIONS_BY_ID.get(appType);
      if (def?.defaultWindowSize) {
        return materializeWindowSizeSpec(def.defaultWindowSize);
      }
    } catch {
      // Fall through to generic default.
    }
  }

  return getDefaultWindowSize();
}

/**
 * Compute a viewport-centered size + non-overlapping position for a new
 * window in one call. This is the highest-traffic geometry helper in the
 * codebase — used by the dock, sidebar, create menus, and most command
 * handlers — so routing it through `computeSmartWindowPosition` (see below)
 * means the large majority of window-open call sites automatically cascade
 * without needing individual changes.
 *
 * Pass `entity` / `entityType` / `appType` when known so entity-specific
 * preferred sizes (e.g. Mail.Thread) are applied.
 */
export const getWindowSizeAndPosition = (opts?: {
  appType?: string;
  entityType?: string;
  entity?: { entityType?: string; type?: string };
}) => {
  const entityType = opts?.entityType || opts?.entity?.entityType || opts?.entity?.type;
  const size = resolveDefaultWindowSize({ appType: opts?.appType, entityType });
  const position = computeSmartWindowPosition(size);
  return { size, position };
};

/**
 * Return a compact landscape size for secondary / folder-scoped windows
 * (e.g. pack-install Drive folder windows) — noticeably smaller than the
 * generic default so they sit beside chat/welcome without dominating.
 *
 * Bounds:
 *   width  — 42 % of viewport, clamped to [520, 820]
 *   height — 52 % of viewport, clamped to [400, 620]
 */
export const getCompactWindowSize = (): { width: number; height: number } => {
  const w = Math.round(Math.min(Math.max(window.innerWidth  * 0.42, 520), 820));
  const h = Math.round(Math.min(Math.max(window.innerHeight * 0.52, 400), 620));
  return toActiveFamilySize(clampToUsableViewport({ width: w, height: h }));
};

/**
 * Return a window size that fills a comfortable fraction of the viewport —
 * used when opening a first-run entity window (e.g. template installs from the
 * intake hero) so the window feels substantial without covering the entire screen.
 *
 * Bounds:
 *   width  — 65 % of viewport, clamped to [680, 1200]
 *   height — 72 % of viewport, clamped to [540, 920]
 */
export const getGenerousWindowSize = (): { width: number; height: number } => {
  const w = Math.round(Math.min(Math.max(window.innerWidth  * 0.65, 680), 1200));
  const h = Math.round(Math.min(Math.max(window.innerHeight * 0.72, 540), 920));
  return toActiveFamilySize(clampToUsableViewport({ width: w, height: h }));
};

/**
 * Return a portrait, document-shaped default size for windows whose content
 * is a page (e.g. newly created Docs) — taller than wide and filling more of
 * the viewport than the generic default, so the page reads naturally instead
 * of looking like a wide, mostly-empty landscape pane.
 *
 * Bounds:
 *   width  — 46 % of viewport, clamped to [720, 880]
 *   height — 86 % of viewport, clamped to [760, 1160]
 *
 * Height is additionally floored at `width + 80` so the window always reads
 * as portrait-oriented, even on short/ultra-wide viewports.
 */
export const getDocumentWindowSize = (): { width: number; height: number } => {
  const width = Math.round(Math.min(Math.max(window.innerWidth * 0.46, 720), 880));
  const height = Math.round(Math.min(Math.max(window.innerHeight * 0.86, 760), 1160));
  return toActiveFamilySize(clampToUsableViewport({ width, height: Math.max(height, width + 80) }));
};

/**
 * Compute the spawn position for a NEW window so it lands centered in the
 * user's current SAFE AREA — the usable space between the system bar and any
 * persistent dock (see `getUsableScreenBounds`) — rather than the raw
 * viewport. Centering on the full viewport would (especially with a large
 * dock, or a dock pinned to the left/right) place the anchor itself outside
 * the safe area before any overlap/bounds logic even runs.
 *
 * In `spatialCanvas` mode window positions are WORLD coordinates under a pan/zoom
 * camera, so the screen-centred default would land at an unpredictable world point
 * (often off-screen). Here we convert the safe-area centre into world space using the
 * per-user camera. All other layouts use plain screen coordinates.
 */
export const getNewWindowSpawnPosition = (
  size: { width: number; height: number },
): { x: number; y: number } => {
  const screenBounds = getUsableScreenBounds();
  // `spatial` windows are stored surface-relative — see `toSurfaceRelativeBounds`.
  const surfaceBounds = toSurfaceRelativeBounds(screenBounds);
  const screenCentered = {
    x: Math.round((surfaceBounds.left + surfaceBounds.right) / 2 - size.width / 2 + getRandomWindowPositionShift()),
    y: Math.round((surfaceBounds.top + surfaceBounds.bottom) / 2 - size.height / 2 + getRandomWindowPositionShift()),
  };

  try {
    const store = useSpaceStore.getState();
    const spaceId = store.activeSpace?.id;
    const viewMode = store.activeSpace?.settings?.viewMode;
    if (viewMode !== 'spatialCanvas' || !spaceId) return screenCentered;

    // `spatialCanvas` renders into `.canvas`, which has NO system-bar inset
    // (its own pan/zoom camera handles all offsetting) — so this branch needs
    // the true screen-space centre, not the surface-relative one above.
    const centerXScreen = (screenBounds.left + screenBounds.right) / 2;
    const centerYScreen = (screenBounds.top + screenBounds.bottom) / 2;
    const camera = useSpaceCameraStore.getState().getCamera(spaceId);
    return {
      x: Math.round((centerXScreen - camera.pan.x) / camera.zoom - size.width / 2),
      y: Math.round((centerYScreen - camera.pan.y) / camera.zoom - size.height / 2),
    };
  } catch {
    return screenCentered;
  }
};

export interface WindowGeometry {
  position: { x: number; y: number };
  size: { width: number; height: number };
}

/**
 * Clamp a window's geometry to the usable viewport (top under the system
 * bar, bottom/left/right at the dock edge or screen edge) — the shared
 * "always within screen bounds" backstop used both for computing a saved
 * focus-maximize restore geometry and for re-clamping a window after the
 * user drags/resizes it (see `finalizeWindowDrag` / `handleResizeStop` /
 * `handleBatchUpdate`), so a window that's already oversized or hanging
 * under the dock is shrunk/repositioned to fit and can never be left
 * off-screen or under the dock.
 *
 * IMPORTANT — `geom.position` is relative to the desktop `.surface` element
 * (see `DesktopShell.module.scss`'s `.surface { inset: 28px 0 0 0; }`),
 * which is *already* offset to start below the system bar. So `y: 0` is
 * already the topmost legal position (flush under the bar) — the system-bar
 * inset must NOT be added again as a floor here, or every window gets
 * pushed `topInset` px lower than it should be (and, since the usable-height
 * ceiling would shift down by the same amount, a window could then be
 * dragged that same distance too far, in under the dock). `.surface` has no
 * left/right inset though (it spans the full viewport width), so `x` bounds
 * genuinely do need `dockInsets.left`/`right` as viewport-relative floors.
 *
 * `opts.allowUnderDock` drops the dock-inset reservation (all sides) while
 * still enforcing the system-bar/screen-edge bounds — used by the live drag
 * clamp and drag-drop finalize so users can deliberately drag a window
 * under/behind a persistent dock, matching native OSes where the dock floats
 * above window content rather than acting as a hard wall. Resize, maximize/
 * edge-snap, and new-window spawn placement are unaffected and keep treating
 * the dock as reserved space.
 */
export function clampWindowGeometry(
  geom: WindowGeometry,
  opts?: { allowUnderDock?: boolean },
): WindowGeometry {
  const topInset = getSystemBarInset();
  const dockInsets = opts?.allowUnderDock ? ZERO_DOCK_INSETS : getDockInsets();
  const usableWidth = window.innerWidth - dockInsets.left - dockInsets.right;
  const usableHeight = window.innerHeight - topInset - dockInsets.bottom;
  const width = Math.min(geom.size.width, usableWidth);
  const height = Math.min(geom.size.height, usableHeight);
  const minX = dockInsets.left;
  const maxX = Math.max(minX, minX + usableWidth - width);
  const x = Math.min(Math.max(geom.position.x, minX), maxX);
  const y = Math.min(Math.max(geom.position.y, 0), Math.max(0, usableHeight - height));
  return { position: { x, y }, size: { width, height } };
}

// Cascade tuning: a new window steps diagonally away from the anchor by this
// many pixels per slot; two windows within OVERLAP_EPSILON of each other on
// both axes are considered "stacked" and rejected. A grid of
// CASCADE_COLUMNS × CASCADE_ROWS candidate slots is tried before giving up —
// comfortably more than the number of windows a user would realistically
// have open at once in a single Space.
const CASCADE_STEP = 32;
const OVERLAP_EPSILON = 24;
const CASCADE_ROWS = 8;
const CASCADE_COLUMNS = 6;

/**
 * Pull a position back inside `bounds` so `size` fits entirely within it.
 * When `size` itself is larger than `bounds` (extreme min/max edge case),
 * pins to the top-left of `bounds` as the best available placement rather
 * than leaving it hanging off an edge. No-op when `bounds` is not provided.
 */
const clampPositionToBounds = (
  pos: { x: number; y: number },
  size: { width: number; height: number },
  bounds?: { left: number; top: number; right: number; bottom: number } | null,
): { x: number; y: number } => {
  if (!bounds) return pos;
  const maxX = Math.max(bounds.left, bounds.right - size.width);
  const maxY = Math.max(bounds.top, bounds.bottom - size.height);
  return {
    x: Math.round(Math.min(Math.max(pos.x, bounds.left), maxX)),
    y: Math.round(Math.min(Math.max(pos.y, bounds.top), maxY)),
  };
};

/**
 * Find a position for a new window of the given `size` that does not land on
 * top of any window in `existing`, preferring positions close to `anchor`.
 *
 * Candidates fan out diagonally from the anchor (macOS-style cascade); once a
 * column of `CASCADE_ROWS` diagonal steps is exhausted (every slot collides or
 * would clip `bounds`), the next column starts back near the anchor's row,
 * offset further to the right — so windows fill the screen in cascading bands
 * rather than marching off a single diagonal line forever.
 *
 * `existing`/`bounds`/`size` must all be in the same coordinate space (screen
 * pixels for `spatial`, world units for `spatialCanvas`) — see
 * `computeSmartWindowPosition` for the space-aware caller.
 *
 * Every return path — including when there's nothing to avoid colliding with
 * — is clamped into `bounds` (when provided) via `clampPositionToBounds`, so
 * a window can never be spawned centered off the usable screen (e.g. under a
 * persistent dock that isn't centered relative to the full viewport).
 *
 * Always terminates: if every candidate slot is occupied (an extreme number
 * of open windows), falls back to the anchor with a small random jitter
 * rather than searching indefinitely.
 */
export const computeNonOverlappingPosition = (
  size: { width: number; height: number },
  existing: WindowGeometry[],
  opts?: {
    anchor?: { x: number; y: number };
    bounds?: { left: number; top: number; right: number; bottom: number } | null;
  },
): { x: number; y: number } => {
  const anchor = opts?.anchor ?? getNewWindowSpawnPosition(size);
  const bounds = opts?.bounds ?? null;
  if (existing.length === 0) return clampPositionToBounds(anchor, size, bounds);

  const withinBounds = (pos: { x: number; y: number }) =>
    !bounds ||
    (pos.x >= bounds.left &&
      pos.y >= bounds.top &&
      pos.x + size.width <= bounds.right &&
      pos.y + size.height <= bounds.bottom);

  const collidesWithExisting = (pos: { x: number; y: number }) =>
    existing.some(
      (w) =>
        Math.abs(w.position.x - pos.x) < OVERLAP_EPSILON &&
        Math.abs(w.position.y - pos.y) < OVERLAP_EPSILON,
    );

  for (let column = 0; column < CASCADE_COLUMNS; column++) {
    for (let row = 0; row < CASCADE_ROWS; row++) {
      const candidate = {
        x: Math.round(anchor.x + row * CASCADE_STEP + column * CASCADE_STEP * (CASCADE_ROWS / 2)),
        y: Math.round(anchor.y + row * CASCADE_STEP),
      };
      if (withinBounds(candidate) && !collidesWithExisting(candidate)) {
        return candidate;
      }
    }
  }

  // Every cascade slot is occupied or would clip the usable viewport — only
  // happens with an extreme number of open windows. Land at the anchor with a
  // small random jitter rather than searching indefinitely.
  return clampPositionToBounds(
    {
      x: Math.round(anchor.x + getRandomWindowPositionShift()),
      y: Math.round(anchor.y + getRandomWindowPositionShift()),
    },
    size,
    bounds,
  );
};

/**
 * The single entry point new windows should use to decide where to appear:
 * viewport-centered, then nudged by `computeNonOverlappingPosition` so it
 * never lands directly on top of an already-open window — and, regardless of
 * how many windows already exist, never outside the usable screen (e.g.
 * under a persistent dock).
 *
 * Reads the active Space's current windows and (for `spatialCanvas`) camera
 * to convert the usable screen bounds into the same coordinate space as the
 * window being placed. In layout families that don't use free-floating
 * geometry (`focused`, `home`, `canvas`, `gui2` — only one window's content is
 * ever visible at a time there) this just returns the centered anchor, since
 * cascading/bounds-clamping has no visual effect when windows aren't rendered
 * spatially.
 */
export const computeSmartWindowPosition = (
  size: { width: number; height: number },
): { x: number; y: number } => {
  const anchor = getNewWindowSpawnPosition(size);

  try {
    const store = useSpaceStore.getState();
    const viewMode = store.activeSpace?.settings?.viewMode;
    const family = layoutFamilyOf(viewMode);
    if (!family) return anchor;

    const existing: WindowGeometry[] = Object.values(store.windows || {})
      .filter((w): w is NonNullable<typeof w> => !!(w && w.position && w.size))
      .map((w) => ({ position: w.position, size: w.size }));

    const screenBounds = getUsableScreenBounds();
    // `spatial` windows are stored surface-relative (see
    // `toSurfaceRelativeBounds`); `spatialCanvas` windows are stored in WORLD
    // units under the pan/zoom camera and need the true screen-space bounds
    // converted through it instead.
    let bounds = toSurfaceRelativeBounds(screenBounds);

    if (family === 'spatialCanvas') {
      const spaceId = store.activeSpace?.id;
      const camera = spaceId
        ? useSpaceCameraStore.getState().getCamera(spaceId)
        : { pan: { x: 0, y: 0 }, zoom: 1 };
      // screen = world * zoom + pan  →  world = (screen - pan) / zoom
      bounds = {
        left: (screenBounds.left - camera.pan.x) / camera.zoom,
        top: (screenBounds.top - camera.pan.y) / camera.zoom,
        right: (screenBounds.right - camera.pan.x) / camera.zoom,
        bottom: (screenBounds.bottom - camera.pan.y) / camera.zoom,
      };
    }

    return computeNonOverlappingPosition(size, existing, { anchor, bounds });
  } catch {
    return anchor;
  }
};

/**
 * Legacy data safety net: `workspace` was removed as a Space viewMode value.
 * Any stray persisted `workspace` (old Spaces saved before the rename, or a
 * stale client cache) is treated as `spatial` — the default working surface —
 * everywhere a viewMode is read, not just at the one call site that happens
 * to render it. Every consumer of a Space's `settings.viewMode` should read
 * it through this normalizer (or through `layoutFamilyOf`, which already
 * applies it) instead of comparing the raw persisted value directly.
 */
export const normalizeViewMode = (viewMode?: string): string | undefined =>
  viewMode === 'workspace' ? 'spatial' : viewMode;

/**
 * Maps a Space viewMode to a layout family key used for per-mode geometry snapshots.
 * Returns null for modes that don't use spatial window geometry (focused, home, canvas, gui2).
 */
export const layoutFamilyOf = (viewMode?: string): 'spatial' | 'spatialCanvas' | null => {
  viewMode = normalizeViewMode(viewMode);
  if (viewMode === 'spatial') return 'spatial';
  if (viewMode === 'spatialCanvas') return 'spatialCanvas';
  return null;
};

/**
 * Compute a non-overlapping grid arrangement for windows that have no saved
 * layout for the given family. Returns a map of windowId → WindowLayout.
 *
 * `spatial` — screen-space coordinates (zoom = 1). Windows are arranged in a
 * left-to-right, top-to-bottom grid that fills the visible area.
 *
 * `spatialCanvas` — world-space coordinates. The current camera (pan + zoom)
 * is read from `spaceCameraStore` so the grid appears centred in the visible
 * canvas viewport. Requires `spaceId` to be passed; without it falls back to
 * the screen-space layout.
 */
export const computeCascadeLayout = (
  windowIds: string[],
  opts?: { topInset?: number; family?: 'spatial' | 'spatialCanvas'; spaceId?: string },
): Record<string, WindowLayout> => {
  const family = opts?.family ?? 'spatial';

  const topInset = opts?.topInset ?? getSystemBarInset();
  // Dock insets are always measured fresh (not overridable via opts) — unlike
  // the system bar, there's no scenario where a caller needs a different
  // dock reservation than what's actually on screen right now.
  const dockInsets = getDockInsets();

  const DEFAULT_WIDTH = 900;
  const DEFAULT_HEIGHT = 680;
  const GAP = 24;

  const vw = typeof window !== 'undefined' ? window.innerWidth  : 1440;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 900;

  const usableW = vw - dockInsets.left - dockInsets.right;
  const usableH = vh - topInset - dockInsets.bottom;

  // How many columns fit in the usable width?
  const cols = Math.max(1, Math.floor((usableW + GAP) / (DEFAULT_WIDTH + GAP)));

  // Grid origin in screen space — centred horizontally within the usable
  // width (so a left/right dock is respected), 60 px below the chrome.
  const totalGridW = cols * DEFAULT_WIDTH + (cols - 1) * GAP;
  const screenOriginX = dockInsets.left + Math.max(GAP, Math.round((usableW - totalGridW) / 2));
  const screenOriginY = topInset + 60;

  // Camera state for spatialCanvas: screen coords → world coords.
  // screen = world * zoom + pan  →  world = (screen - pan) / zoom
  let panX = 0;
  let panY = 0;
  let zoom = 1;

  if (family === 'spatialCanvas' && opts?.spaceId) {
    try {
      const { spaceCameraStore } = require('../../stores/spaceCameraStore');
      const cam = spaceCameraStore.getCamera(opts.spaceId);
      panX = cam.pan?.x ?? 0;
      panY = cam.pan?.y ?? 0;
      zoom = cam.zoom ?? 1;
      if (!zoom || zoom <= 0) zoom = 1;
    } catch { /* fall back to screen-space (zoom = 1, pan = 0) */ }
  }

  // Dimensions in world units (for canvas mode zoom ≠ 1 makes windows physically
  // smaller in world space so they appear at the correct screen size).
  const worldW   = Math.round(DEFAULT_WIDTH  / zoom);
  const worldH   = Math.round(DEFAULT_HEIGHT / zoom);
  const worldGap = Math.round(GAP / zoom);

  // Origin in the coordinate space `window.position` is actually stored in
  // for each family. `spatialCanvas` renders into `.canvas` (no system-bar
  // inset of its own — the pan/zoom camera handles all offsetting), so its
  // world origin is the true screen-space origin converted through the
  // camera, unchanged. `spatial` windows are stored relative to `.surface`,
  // which already starts below the system bar — subtracting `topInset` back
  // out of `screenOriginY` here avoids double-counting it (once from
  // `.surface`'s own CSS inset, once from `screenOriginY` baking it in),
  // which would otherwise push the grid's last row `topInset` px past the
  // real dock boundary. `originX` needs no such correction: `.surface` has no
  // horizontal inset, and `panX`/`zoom` are fixed at 0/1 for this family.
  const originX = (screenOriginX - panX) / zoom;
  const originY = family === 'spatialCanvas' ? (screenOriginY - panY) / zoom : screenOriginY - topInset;

  // How many rows fit on screen.
  const maxRows = Math.max(1, Math.floor((usableH - 60 + GAP) / (DEFAULT_HEIGHT + GAP)));

  const result: Record<string, WindowLayout> = {};

  windowIds.forEach((id, i) => {
    // After cols × maxRows windows, start a second "page" slightly offset.
    const page    = Math.floor(i / (cols * maxRows));
    const pageIdx = i % (cols * maxRows);
    const col     = pageIdx % cols;
    const row     = Math.floor(pageIdx / cols);

    const x = originX + col * (worldW + worldGap) + page * Math.round(worldW * 0.25);
    const y = originY + row * (worldH + worldGap) + page * Math.round(worldH * 0.25);

    result[id] = {
      position: { x: Math.round(x), y: Math.round(y) },
      size: { width: worldW, height: worldH },
      zIndex: i + 1,
    };
  });

  return result;
};

export const fileAndGroupFilter = (entity: BaseEntityType) =>
  (entity.entityType === 'File' || entity.entityType?.startsWith('File.') || entity.entityType === 'Group') && !entity.isSystemGroup;

export const getWindowTitle = (window: WindowEntity): string => {
  if (!window.appType) return window.title || 'Untitled';

  const windowAny = window as any;

  let currentEntity = null;
  if (window.entityLlmId) {
    currentEntity = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find((e: any) => e.llmId === window.entityLlmId);
  }
  if (!currentEntity) {
    currentEntity = windowAny.entity;
  }

  // ── Title priority ────────────────────────────────────────────────────────
  // `window.title` is authoritative: it is CRDT-persisted and kept in sync with
  // the entity name by `subscribeToChanges`. `currentEntity.name` (from the
  // in-memory registry) may be stale when loaded from CRDT before the REST
  // fetch for the entity has completed. Prefer a custom (non-canonical)
  // `window.title` over entity name, mirroring `getWindowTitleWithEntity`.
  const canonicalTitle = getAppTitleByType(window.appType);
  const autoGeneratedTitle = window.appType.charAt(0).toUpperCase() + window.appType.slice(1);
  const hasCustomWindowTitle = !!(
    window.title &&
    window.title !== canonicalTitle &&
    window.title !== autoGeneratedTitle &&
    window.title !== 'Untitled Document' &&
    window.title !== 'Documents'
  );

  const windowWithState = window as WindowWithApplicationState;
  if (window.appType === 'docs' && windowWithState.applicationState?.docs) {
    const docsState = windowWithState.applicationState.docs;

    if (docsState.currentState === 'editing' && docsState.activeDocId) {
      if (hasCustomWindowTitle) return window.title!;
      if (currentEntity?.name) return currentEntity.name;
      const entityByDocId = (entityRegistry.dumpAll().flatMap((__s) => __s.entities) as any[]).find(
        (e: any) => e._id === docsState.activeDocId || e.id === docsState.activeDocId
      );
      if (entityByDocId?.name) return entityByDocId.name;
      return 'Untitled Document';
    }

    if (docsState.currentState === 'overview') return 'Documents';
  }

  if (hasCustomWindowTitle) return window.title!;
  if (currentEntity?.name) return currentEntity.name;
  return canonicalTitle;
};

/**
 * Resolve a window title using a pre-fetched entity — avoids the expensive
 * entityRegistry.dumpAll().flatMap().find() scan that getWindowTitle does.
 * Call this from useWindowTitle; for one-off non-hook usage keep getWindowTitle.
 */
export const getWindowTitleWithEntity = (
  win: WindowEntity | null | undefined,
  entity: BaseEntityType | null | undefined,
): string => {
  if (!win) return 'Untitled';
  if (!win.appType) return win.title || 'Untitled';

  // ── Title priority order ─────────────────────────────────────────────────
  //
  // `win.title` is the authoritative stored title. It is kept in sync with the
  // entity name by the `subscribeToChanges` listener in spaceStore and is
  // persisted to CRDT/IDB on every rename. `entity.name` (from useEntityById)
  // may be stale from the entity-by-id IDB until the REST fetch completes.
  //
  // We therefore prefer a custom (non-canonical) `win.title` over `entity.name`
  // so refreshing always shows the correct title immediately. `entity.name` is
  // still used as a fallback when `win.title` is missing or canonical.

  const canonicalTitle = getAppTitleByType(win.appType);
  const autoGeneratedTitle = win.appType.charAt(0).toUpperCase() + win.appType.slice(1);
  const hasCustomWindowTitle = !!(
    win.title &&
    win.title !== canonicalTitle &&
    win.title !== autoGeneratedTitle &&
    win.title !== 'Untitled Document' &&
    win.title !== 'Documents'
  );

  // Docs: applicationState.docs lets us show "Documents" on the overview screen.
  const windowWithState = win as WindowWithApplicationState;
  if (win.appType === 'docs' && windowWithState.applicationState?.docs) {
    const docsState = windowWithState.applicationState.docs;
    if (docsState.currentState === 'editing' && docsState.activeDocId) {
      // Prefer the CRDT-persisted win.title when it's a real (custom) name.
      // Fall back to entity.name (may arrive async from REST) and then the
      // default "Untitled Document".
      if (hasCustomWindowTitle) return win.title!;
      if (entity?.name) return entity.name;
      return 'Untitled Document';
    }
    if (docsState.currentState === 'overview') return 'Documents';
  }

  // For all other entity-backed windows: prefer custom win.title, then entity.name.
  if (hasCustomWindowTitle) return win.title!;
  if (entity?.name) return entity.name;

  // Static / app-type title
  if (win.title && win.title !== canonicalTitle && win.title !== autoGeneratedTitle) {
    return win.title;
  }
  return canonicalTitle;
};

/**
 * React hook: resolve the display title for a window.
 *
 * Uses useEntityById for an O(1) indexed lookup instead of the expensive
 * entityRegistry.dumpAll().flatMap().find() used by getWindowTitle().
 * Subscribes only to this window's slice + its entity — other windows
 * updating never trigger a re-render here.
 */
export function useWindowTitle(windowId: string): string {
  const win = useSpaceStore((s) => s.windows[windowId]);
  const entity = useEntityById(win?.entityId);
  return useMemo(
    () => getWindowTitleWithEntity(win, entity ?? null),
    // Key inputs that can change the title:
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [win?.title, win?.appType, win?.entityId, (win?.applicationState as any)?.docs?.currentState, (win?.applicationState as any)?.docs?.activeDocId, entity?.name],
  );
}

/**
 * Resolve the display icon for a window pane, shared by WindowEntityTabs and
 * any other UI that lists open windows (e.g. the chat OpenWindowsIndicator).
 *
 * Priority:
 *   1. Content entities always use the entity icon (via the caller's
 *      `getEntityIcon` from `useEntityIcon`); UI-shell entities (Window/
 *      NewWindow launcher, AppInstance) get their own icons instead.
 *   2. Whenever the entity isn't resolved (blank app window, or an entity
 *      reference that hasn't finished loading yet) → squircle icon by
 *      appType. An app-specific icon is always more identifiable than a
 *      generic file icon, so this is preferred even when an entity
 *      reference is technically present but not yet resolved.
 *   3. Fallback: generic file icon, only when the appType has no known icon.
 *
 * `opts.flat` renders a bare app icon with no squircle container (background/
 * clip-path/shadow) — for UI that already wraps the icon in its own chip and
 * would otherwise show a nested "icon box within a box" (e.g. the chat
 * OpenWindowsIndicator's primary chip; its hover popover rows omit this and
 * keep the normal squircle look).
 */
export function getWindowDisplayIcon(
  window: AnyWindowEntity,
  getEntityIcon: (entity: BaseEntityType, opts?: { flat?: boolean }) => React.ReactNode,
  size: number = 14,
  opts?: { flat?: boolean },
): React.ReactNode {
  const flat = opts?.flat;
  const windowEntity = useSpaceStore.getState().getWindowEntity(window.id);

  if (windowEntity) {
    if (windowEntity.entityType === 'Window' || windowEntity.entityType === 'NewWindow') {
      return React.createElement(Icon, {
        icon: { type: 'phosphor', phosphorIcon: 'GridFour', phosphorWeight: 'duotone', metadata: { isAppIcon: false } },
        size: 'small',
      });
    }

    const isUIEntity = ['SearchResults'].includes(windowEntity.entityType);
    if (!isUIEntity) {
      if (windowEntity.entityType === 'AppInstance' || windowEntity.entityType === 'System.AppInstance') {
        return React.createElement(AppSquircle, { appId: (windowEntity.skeleton as any)?.appId || '', size: 'sm', flat });
      }
      return getEntityIcon(windowEntity, { flat });
    }
  }

  if (window.appType && iconService.hasAppIcon(window.appType)) {
    return React.createElement(AppSquircle, { appId: window.appType, size: 'sm', flat });
  }

  return React.createElement(File, { size, weight: 'fill' });
}

export const getMentionDisplayName = (entity: AnyWindowEntity): string => {
  if (!entity) return 'Unknown';
  
  // For document windows, use the same logic as getWindowTitle to get the current document name
  const entityAny = entity as any;
  if (entityAny.appType === 'docs' && entityAny.applicationState?.docs) {
    const docsState = entityAny.applicationState.docs;
    
    // If we're editing a document, use its current name
    if (docsState.currentState === 'editing' && docsState.currentDocName) {
      return docsState.currentDocName;
    }
    
    // If we have an activeDocId but no currentDocName, try to find it in recentDocs
    if (docsState.activeDocId && docsState.recentDocs) {
      const activeDoc = docsState.recentDocs.find((doc: any) => doc.docId === docsState.activeDocId);
      if (activeDoc && activeDoc.name) {
        return activeDoc.name;
      }
    }
  }
  
  // For other entity types, use the existing logic
  if ('title' in entity && typeof entity.title === 'string') {
    return entity.title || 'Untitled';
  }
  
  if ('name' in entity && typeof entity.name === 'string') {
    return entity.name;
  }

  return entity.id || 'Unknown';
};

/**
 * Focus an existing window of the given appType, or create a new one if none exists.
 * spaceStore.bringWindowToFront raises both the active window and the spatialUIStore
 * z-order internally.
 */
export async function focusOrCreateAppWindow(
  appType: string,
  createConfig: {
    appType: string;
    title?: string;
    position?: { x: number; y: number };
    size?: { width: number; height: number };
    [key: string]: unknown;
  }
): Promise<void> {
  const { windows, actions, bringWindowToFront } = useSpaceStore.getState();
  const existing = Object.values(windows).find(w => w.appType === appType);
  if (existing) {
    bringWindowToFront(existing.id);
    return;
  }
  const windowId = await actions.createWindow(createConfig);
  actions.setActiveWindow(windowId);
}